import {get, omit} from "lodash"

import API, {APIRequestConfig} from "@irati/utils/services/API"
import client, {QueryOptions} from "@irati/utils/services/apollo"
import PBService from "@irati/utils/services"
import getShortKey from "@irati/utils/getShortKey"
import {QueryDealArgs, QueryDealBySlugArgs, Deal, QueryDealsArgs} from "@irati/types"

import endpoints from "../endpoints"
import dealsErrorHandler from "./errors/dealsErrorHandler"
import dealQuery, {dealQueryKey} from "./queries/deal"
import dealBySlugQuery, {dealBySlugQueryKey} from "./queries/dealBySlug"
import dealListQuery, {dealListQueryKey} from "./queries/dealList"

export interface Locale {
  id: string,
  language: string,
  country: string,
  dialect: string,
  name: string
}

class DealService extends PBService {
  getDealByUrlSlug(variables: QueryDealBySlugArgs, options: QueryOptions = {}) {
    return client.query({...options, query: dealBySlugQuery, variables}).then(({data}) => data[dealBySlugQueryKey])
  }

  getDeal(variables: Partial<QueryDealArgs>, options: QueryOptions = {}) {
    return client.query({...options, query: dealQuery, variables}).then(({data}) => data[dealQueryKey])
  }

  getDealList(variables: QueryDealsArgs, options: QueryOptions = {}) {
    return client.query({...options, query: dealListQuery, variables}).then(({data}) => data[dealListQueryKey])
  }

  internalGetDeal(uuid: string, config: APIRequestConfig = {}) {
    let url = endpoints.deal(uuid)
    if (config.expand) {
      url += "?expand=true"
    }
    return API.get(url, config).then((res) => {
      hack(res.data)
      if (config.returnResponse) {
        return res
      }
      return res.data
    })
  }

  getDeals(params) {
    return API.get(endpoints.deals(), {params}).then((res) => {
      const {data} = res
      return data
    })
  }

  getAvailableLocales(): Promise<Locale[]> {
    return API.get(endpoints.dealLocales()).then((res) => {
      const {data} = res
      return data.data
    })
  }

  createDeal(data) {
    return API.post(endpoints.deals(), data).then((res) => {
      const uuid = getShortKey(res.data.links.self, "/v1/deals/")
      return this.internalGetDeal(uuid).then((updatedDeal) => updatedDeal)
    })
  }

  saveDeal(uuid: string, deal: Deal, etag = deal.Etag, config = {}) {
    // US-1074 - Hopefully a temp check.
    // Should be addressed by the "deal deduplication project", where the reviewState will be visible to the user.
    deal.reviewState = deal.status === "OFF" ? "REJECTED" : "APPROVED"
    return API.put(endpoints.deal(uuid), parseForPut(deal), etag, config).then(() => {
      return this.internalGetDeal(uuid)
    })
  }
}

const service = new DealService({errorHandlers: [dealsErrorHandler]})

export default service

export const getDealByUrlSlug = service.getDealByUrlSlug
export const getDeal = service.getDeal
export const internalGetDeal = service.internalGetDeal
export const getDeals = service.getDeals
export const getAvailableLocales = service.getAvailableLocales
export const createDeal = service.createDeal
export const saveDeal = service.saveDeal

const parseForPut = (deal: Deal) => {
  deal.endsAt = deal.endsAt || null
  return omit(deal, ["uuid", "date", "dateUnix", "popularity", "tenant"])
}

function hack(deal: Deal) {
  const value = get(deal, "savings.userSavings.value", null)
  if (value) {
    deal.savings.userSavings.value = `${value}`
  }
}
